type FormatRule = { pattern: RegExp; description: string }

const METER_FORMAT_RULES: Record<string, FormatRule> = {
    'PG&E': { pattern: /^\d{10}$/, description: '10 digits (e.g. 1009537528)' },
    'SDG&E': { pattern: /^0\d{7}$/, description: '8 digits with leading zero (e.g. 06172333)' },
    SCE: {
        // Keep this identical to libs/meter-format validateMeterNumberFormat (SCE). Admin does not import that package.
        pattern: /^(?:[A-Z][A-Z0-9]+-\d{6}|\d{6}-\d{6})$/,
        description:
            '6-digit groups (e.g., 256000-007268) or an uppercase letter, then letters or digits, a dash, and 6 digits (e.g., ZYA085-295592)',
    },
}

const UA_FORMAT_RULES: Record<string, FormatRule> = {
    'PG&E': { pattern: /^\d{10}-\d$/, description: '10 digits + dash + 1 digit (e.g. 6885779474-4)' },
    'SDG&E': {
        pattern: /^(00\d{10,11}|21000\d{7,8})$/,
        description: '12 or 13 digits starting with 00 or 21000 (e.g. 2100005407889)',
    },
    SCE: { pattern: /^(60|70)\d{10}$/, description: '12 digits starting with 60 or 70 (e.g. 700936309116)' },
}

const SA_FORMAT_RULES: Record<string, FormatRule> = {
    'PG&E': { pattern: /^\d{8,10}$/, description: '8, 9, or 10 digits (e.g. 5293186292)' },
    'SDG&E': { pattern: /^\d{10}$|^0+$/, description: '10 digits (e.g. 4010597053) or all zeros' },
    SCE: { pattern: /^8\d{9}$/, description: '10 digits starting with 8 (e.g. 8003548859)' },
}

function checkFormat(value: string, utilityShortName: string, rules: Record<string, FormatRule>): { valid: boolean; expected: string | null } {
    const rule = rules[utilityShortName]
    if (!rule) return { valid: true, expected: null }
    const valid = rule.pattern.test(value)
    return { valid, expected: valid ? null : rule.description }
}

function toWarning(
    value: string | null | undefined,
    utilityShortName: string | null | undefined,
    rules: Record<string, FormatRule>
): string | null {
    if (value == null || value === '') return null
    if (!utilityShortName) return null
    const { valid, expected } = checkFormat(value, utilityShortName, rules)
    if (valid) return null
    return `Expected ${expected} for ${utilityShortName}`
}

export function getMeterNumberWarning(
    meterNumber: string | null | undefined,
    utilityShortName: string | null | undefined
): string | null {
    return toWarning(meterNumber, utilityShortName, METER_FORMAT_RULES)
}

export function getUtilityAccountNumberWarning(
    uaNum: string | null | undefined,
    utilityShortName: string | null | undefined
): string | null {
    return toWarning(uaNum, utilityShortName, UA_FORMAT_RULES)
}

export function getServiceAgreementNumberWarning(
    saNum: string | null | undefined,
    utilityShortName: string | null | undefined
): string | null {
    return toWarning(saNum, utilityShortName, SA_FORMAT_RULES)
}

export function isValidSalesforceId(id: string | null | undefined): boolean {
    if (id == null || id === '') return true
    return /^[a-zA-Z0-9]{15}$|^[a-zA-Z0-9]{18}$/.test(id)
}

export function getSalesforceIdWarning(id: string | null | undefined): string | null {
    if (id == null || id === '') return null
    if (isValidSalesforceId(id)) return null
    return 'Expected 15 or 18 alphanumeric characters'
}
